import { AddressDetail, BankAccountDetail, InfoState, PersonalDetail } from '@/types/InfoState';
import { HomeRetrieveProposalResponse } from '@/types/Proposal';
import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { format, parseISO } from 'date-fns';

const initialState: InfoState = {
  personalDetail: {
    name: '',
    idType: 0,
    idNumber: '',
    dateOfBirth: '',
    gender: '',
    ethnicity: 0,
    mobileNo: '',
    email: '',
    bankId: '',
    bankName: '',
    bankAccountNumber: '',
    nationality: undefined,
  },
  propertyDetail: {
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    country: '',
    postcode: '',
  },
  mailingDetail: {
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    country: '',
    postcode: '',
    propertySameAddressCheck: false,
  },
  bankAccountDetail: {
    bankId: 0,
    accountHolder: '',
    accountNo: '',
  },
  loading: false,
  error: false,
};

export const infoStateSlice = createSlice({
  name: 'info',
  initialState,
  reducers: {
    resetInfoState: () => {
      return initialState;
    },
    updateDateOfBirth: (state, action: PayloadAction<string>) => {
      state.personalDetail.dateOfBirth = action.payload;
    },
    updatePersonalDetail: (state, action: PayloadAction<PersonalDetail>) => {
      const data = action.payload;
      state.personalDetail = {
        name: data.name,
        idType: data.idType,
        idNumber: data.idNumber,
        dateOfBirth: data.dateOfBirth,
        gender: data.gender,
        ethnicity: data.ethnicity,
        mobileNo: '0' + data.mobileNo,
        email: data.email,
        bankId: data.bankId,
        bankName: data.bankName,
        bankAccountNumber: data.bankAccountNumber,
        nationality: data.nationality,
      };

      state.bankAccountDetail.accountHolder = data.name;
    },
    updatePropertyAddressDetail: (state, action: PayloadAction<AddressDetail>) => {
      const data = action.payload;
      state.propertyDetail = {
        addressLine1: data.addressLine1,
        addressLine2: data.addressLine2,
        city: data.city,
        state: data.state,
        country: data.country,
        postcode: data.postcode,
      };
    },
    updateMailingAddressDetail: (state, action: PayloadAction<AddressDetail>) => {
      const data = action.payload;
      state.mailingDetail = {
        addressLine1: data.addressLine1,
        addressLine2: data.addressLine2,
        city: data.city,
        state: data.state,
        country: data.country,
        postcode: data.postcode,
        propertySameAddressCheck: data.propertySameAddressCheck,
      };
    },
    updateBankDetail: (state, action: PayloadAction<BankAccountDetail>) => {
      const data = action.payload;
      state.bankAccountDetail.bankId = data.bankId;
      state.bankAccountDetail.accountNo = data.accountNo;
    },
    mapProposalDataToInfoState: (state, action: PayloadAction<HomeRetrieveProposalResponse>) => {
      const data = action.payload;

      state.bankAccountDetail = {
        bankId: data.bankId,
        accountHolder: data.accountHolder,
        accountNo: data.accountNo,
      };

      state.mailingDetail = {
        addressLine1: data.policyOwner.address?.addressLine1,
        addressLine2: data.policyOwner.address?.addressLine2,
        postcode: data.policyOwner.address?.postcode,
        city: data.policyOwner.address?.city,
        state: data.policyOwner.address?.state,
        country: data.policyOwner.address?.country,
      };

      state.propertyDetail = {
        addressLine1: data.home.address.address1,
        addressLine2: data.home.address.address2,
        postcode: data.home.address.postcode,
        city: data.home.address.city,
        state: data.home.address.state,
        country: data.home.address.country,
      };

      state.personalDetail = {
        name: data.policyOwner.name,
        idType: Number(data.policyOwner.identityType),
        idNumber: data.policyOwner.identityNumber,
        nationality: data.policyOwner.nationality,
        dateOfBirth: format(parseISO(data.policyOwner.birthDate), 'dd/MM/yyyy'),
        gender: data.policyOwner.gender,
        ethnicity: data.policyOwner.ethnicity,
        mobileNo: data.policyOwner.mobile,
        email: data.policyOwner.email,
      };
    },
  },
});

export const {
  updateDateOfBirth,
  resetInfoState,
  updatePersonalDetail,
  updatePropertyAddressDetail,
  updateMailingAddressDetail,
  updateBankDetail,
  mapProposalDataToInfoState,
} = infoStateSlice.actions;

export default infoStateSlice.reducer;
